import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FEATURES } from '@/config/recentFeatures';
import { Sparkles, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * "Try this feature" guided spotlight.
 *
 * Activated by `?tour=<featureId>` in the URL. The drawer link adds this
 * query param when navigating, which lands the user on the feature's page
 * AND surfaces a pulsing ring + tooltip pointing at the relevant DOM
 * element (looked up via the feature entry's `targetTestId`).
 *
 * Auto-dismisses on:
 *   - Click anywhere outside the highlighted element
 *   - Esc key
 *   - "Got it" button click
 *   - Route change (the query param falls off naturally)
 */
const TOOLTIP_ID = 'smarto-feature-tour-tooltip';

export const FeatureTourHighlight = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  const [feature, setFeature] = useState(null);
  const [rect, setRect] = useState(null);
  const { t } = useLanguage();

  // Parse ?tour= and look up the feature
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('tour');
    if (!id) { setFeature(null); setTarget(null); return; }
    const f = FEATURES.find((x) => x.featureId === id);
    if (!f || !f.targetTestId) { setFeature(null); setTarget(null); return; }
    setFeature(f);
    // Wait for DOM mount — try several times since the page may still be loading
    let attempts = 0;
    const findEl = () => {
      const el = document.querySelector(`[data-testid="${f.targetTestId}"]`);
      if (el) {
        setTarget(el);
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
        return true;
      }
      return false;
    };
    if (!findEl()) {
      const interval = setInterval(() => {
        attempts += 1;
        if (findEl() || attempts >= 10) clearInterval(interval);
      }, 400);
      return () => clearInterval(interval);
    }
  }, [location.pathname, location.search]);

  // Track target's bounding rect (re-measure on scroll/resize)
  useEffect(() => {
    if (!target) { setRect(null); return; }
    const measure = () => {
      const r = target.getBoundingClientRect();
      setRect({
        top: r.top, left: r.left, width: r.width, height: r.height,
      });
    };
    measure();
    const onScroll = () => measure();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [target]);

  // Esc to dismiss
  useEffect(() => {
    if (!feature) return;
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature]);

  const dismiss = () => {
    const params = new URLSearchParams(location.search);
    params.delete('tour');
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
    setFeature(null);
    setTarget(null);
    setRect(null);
  };

  if (!feature || !rect) return null;

  // Position tooltip below the target if there's room, otherwise above.
  const PAD = 8;
  const TOOLTIP_W = 320;
  const TOOLTIP_H = 110;
  const spaceBelow = window.innerHeight - (rect.top + rect.height);
  const placeBelow = spaceBelow > TOOLTIP_H + PAD * 2;
  const tooltipTop = placeBelow ? rect.top + rect.height + PAD : rect.top - TOOLTIP_H - PAD;
  const tooltipLeft = Math.max(
    PAD,
    Math.min(rect.left + rect.width / 2 - TOOLTIP_W / 2, window.innerWidth - TOOLTIP_W - PAD)
  );

  return (
    <div
      data-testid="feature-tour-overlay"
      className="fixed inset-0 z-[80] pointer-events-none"
      aria-live="polite"
    >
      {/* Backdrop with a hole punched at the target. */}
      <div
        className="absolute inset-0 bg-stone-900/40 backdrop-blur-[1px] pointer-events-auto"
        onClick={dismiss}
        data-testid="feature-tour-backdrop"
      />
      {/* Pulsing ring around the target. */}
      <div
        data-testid="feature-tour-ring"
        className="absolute rounded-2xl ring-4 ring-orange-500 ring-offset-2 ring-offset-white/0 shadow-2xl pointer-events-none animate-pulse"
        style={{
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          // Cut the dark backdrop using a box-shadow trick — gives the
          // illusion of a spotlight on the element.
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
        }}
      />
      {/* Tooltip. */}
      <div
        id={TOOLTIP_ID}
        role="tooltip"
        data-testid="feature-tour-tooltip"
        className="absolute pointer-events-auto bg-white dark:bg-stone-800 rounded-xl shadow-2xl border border-stone-200 dark:border-stone-700 p-4"
        style={{ top: tooltipTop, left: tooltipLeft, width: TOOLTIP_W }}
      >
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center flex-shrink-0">
              <Sparkles size={11} className="text-white" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-orange-600">{t('whats_new_try_feature')}</span>
          </div>
          <button
            type="button"
            onClick={dismiss}
            data-testid="feature-tour-close-btn"
            className="p-1 rounded hover:bg-stone-100 dark:hover:bg-stone-700 transition -mt-1 -mr-1"
            aria-label={t('whats_new_close')}
          >
            <X size={12} />
          </button>
        </div>
        <h4 className="text-sm font-bold font-outfit leading-tight">{feature.title}</h4>
        {feature.tourHint && (
          <p className="text-xs text-muted-foreground mt-1 leading-snug">{feature.tourHint}</p>
        )}
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={dismiss}
            data-testid="feature-tour-gotit-btn"
            className="text-xs font-semibold px-3 py-1.5 rounded-md bg-orange-500 text-white hover:bg-orange-600 transition"
          >
            {t('whats_new_got_it')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeatureTourHighlight;
